import { Fragment } from 'react';
import { useFormikContext } from 'formik';
import cc from 'classcat';

import { template } from '#src/lib/template';
import { isGameBetOrder as isGameBetOrderSelector } from '#src/lib/selectors/order';
import {
  LegalStatement,
  OldPlatformLegalStatement,
  PaymentAndRegistrationForm,
  SubmittingOverlay,
} from '#src/components/Cart';
import Container from '#src/components/Container';
import { Markdown } from '#src/components/Markdown';
import Notification from '#src/components/Notification';
import { From } from '#src/components/Responsive';
import SiblingWrapper from '#src/components/SiblingWrapper';

import { withCheckoutForm } from '../withCheckoutForm';

import Discount from './Discount';
import GameItem from './GameItem';
import { MembershipItem } from './MembershipItem';
import Total from './Total';
import styles from './styles.module.scss';

const Component = ({
  amountDueLabel,
  betAmountLabel,
  bets,
  className,
  ctaLabel,
  disabled,
  discounts,
  games,
  guaranteeDays,
  guaranteeStatement,
  hasFreeTrial,
  hasMembershipInCart,
  hasPromoCodes,
  isAdHocMembershipOrder,
  legalStatement,
  membershipContent,
  membershipDisclaimer,
  memberships,
  onPromoError,
  order,
  orderId,
  paidNotification,
  removeDiscountLabel,
  removeItem,
  removeItemLabel,
  requiresMembershipInCart,
  requiresRegistration,
  savePaymentLabel,
  selectPaymentPlan,
  setBraintreeInstance,
  setPaymentMethodReady,
  theme,
  token,
}) => {
  const { isSubmitting } = useFormikContext();
  const { amountDue } = order;

  const isGameBetOrder = isGameBetOrderSelector(order);
  const isOrderFinal = order.status == 'final';
  const showGuaranteeStatement =
    !hasFreeTrial && !isAdHocMembershipOrder && !isGameBetOrder;
  const combinedGames = [...(games || []), ...(bets || [])];

  return (
    <Fragment>
      {isSubmitting && <SubmittingOverlay />}
      <Container
        className={cc([styles.container, className])}
        outerClassName={styles.outerContainer}
      >
        {isOrderFinal && <Notification>{paidNotification}</Notification>}
        <From slice={3}>
          {(matches) => (
            <div className={styles.cart}>
              {combinedGames.map(
                ({ amount, game, paymentPlan, paymentPlanChoices }) => (
                  <SiblingWrapper key={game.id} container={matches}>
                    <GameItem
                      amount={amount}
                      content={{ betAmountLabel }}
                      game={game}
                      paymentPlan={paymentPlan}
                      paymentPlanChoices={paymentPlanChoices}
                      className={styles.item}
                      onPlanSelection={selectPaymentPlan}
                      theme={theme}
                    />
                  </SiblingWrapper>
                )
              )}
              {memberships?.map?.(({ amount, content, id }) => (
                <SiblingWrapper key={id} container={matches}>
                  <MembershipItem
                    className={styles.item}
                    content={{
                      memberships: membershipContent,
                      removeItemLabel,
                    }}
                    isRemovable={
                      !isAdHocMembershipOrder && !requiresMembershipInCart
                    }
                    itemContent={content}
                    itemId={id}
                    onRemove={removeItem}
                    theme={theme}
                    amount={amount}
                  />
                </SiblingWrapper>
              ))}
              <SiblingWrapper container={matches}>
                {discounts.map((item) => (
                  <Discount
                    key={item.id}
                    theme={theme}
                    content={{ removeDiscountLabel }}
                    item={item}
                    onRemove={removeItem}
                  />
                ))}
                <Total
                  theme={theme}
                  orderId={orderId}
                  amountDue={amountDue}
                  content={{ amountDueLabel }}
                  hidePromoForm={hasPromoCodes || isOrderFinal}
                  onPromoError={onPromoError}
                />
              </SiblingWrapper>
            </div>
          )}
        </From>
        {showGuaranteeStatement && (
          <Markdown escapeHtml={false} className={styles.message}>
            {template(guaranteeStatement, { guaranteeDays })}
          </Markdown>
        )}

        {!isOrderFinal && (
          <PaymentAndRegistrationForm
            ctaLabel={isGameBetOrder ? 'Place bet' : ctaLabel}
            ctaDisabled={disabled}
            isAdHocMembershipOrder={isAdHocMembershipOrder}
            order={order}
            requiresRegistration={requiresRegistration}
            savePaymentLabel={savePaymentLabel}
            setBraintreeInstance={setBraintreeInstance}
            setPaymentMethodReady={setPaymentMethodReady}
            theme={theme}
            token={token}
          />
        )}

        {(!!isAdHocMembershipOrder || hasMembershipInCart) && (
          <OldPlatformLegalStatement
            membershipDisclaimer={membershipDisclaimer}
            theme={theme}
          />
        )}
        {!!combinedGames.length && !!legalStatement && (
          <LegalStatement
            games={combinedGames}
            legalStatement={legalStatement}
            theme={theme}
          />
        )}
      </Container>
    </Fragment>
  );
};

export const Checkout = withCheckoutForm(Component);
